interface Variant {
  id: number
  price: number
  available: boolean
  options: string[]
  title: string
  featured_media_id: number | null
}

declare global {
  interface Window { __pdpVariants?: Variant[] }
}

export function initPDP(): void {
  const pdp = document.querySelector<HTMLElement>('[data-pdp]')
  if (!pdp) return

  const thumbs = pdp.querySelectorAll<HTMLButtonElement>('[data-pdp-thumb]')
  const slides = pdp.querySelectorAll<HTMLElement>('[data-pdp-slide]')

  function activateMediaByIndex(idx: string | undefined): void {
    if (!idx) return
    thumbs.forEach((x) => x.classList.toggle('is-active', x.dataset.index === idx))
    slides.forEach((s) => {
      const isActive = s.dataset.index === idx
      s.classList.toggle('is-active', isActive)
      if (s instanceof HTMLVideoElement) {
        if (isActive) {
          s.play().catch(() => {
            // Autoplay can be blocked even when muted in some contexts; ignore
          })
        } else {
          s.pause()
        }
      }
    })
  }

  function activateMediaById(mediaId: number | null | undefined): void {
    if (!mediaId) return
    const targetSlide = pdp!.querySelector<HTMLElement>(`[data-pdp-slide][data-media-id="${mediaId}"]`)
    if (!targetSlide) return
    activateMediaByIndex(targetSlide.dataset.index)
  }

  thumbs.forEach((t) => {
    t.addEventListener('click', () => {
      activateMediaByIndex(t.dataset.index)
      // If this thumb's media maps to a variant, sync the swatch selection
      // so title / price / Add to Bag all update along with the image.
      const mediaId = Number(t.dataset.mediaId)
      if (mediaId) syncVariantFromMediaId(mediaId)
    })
  })

  // Metafield color swatches (Brodi-style: no real variants, just a color metafield)
  const colorSwaps = pdp.querySelectorAll<HTMLButtonElement>('[data-pdp-color-swap]')
  colorSwaps.forEach((btn) => {
    btn.addEventListener('click', () => {
      colorSwaps.forEach((x) => x.classList.toggle('is-selected', x === btn))
      activateMediaByIndex(btn.dataset.mediaIndex)
    })
  })

  const variantData = window.__pdpVariants
  const options = pdp.querySelectorAll<HTMLElement>('[data-pdp-option]')
  const variantInput = pdp.querySelector<HTMLInputElement>('[data-pdp-variant-input]')
  const priceEl = pdp.querySelector<HTMLElement>('[data-pdp-price]')
  const addBtn = pdp.querySelector<HTMLButtonElement>('[data-pdp-add]')
  const subtitleEl = pdp.querySelector<HTMLElement>('[data-pdp-variant-subtitle]')
  const notifyForm = pdp.querySelector<HTMLFormElement>('[data-pdp-notify]')
  const notifyVariant = pdp.querySelector<HTMLInputElement>('[data-pdp-notify-variant]')
  const notifyVariantId = pdp.querySelector<HTMLInputElement>('[data-pdp-notify-variant-id]')
  const notifyBody = pdp.querySelector<HTMLInputElement>('[data-pdp-notify-body]')
  const notifySuccess = pdp.querySelector<HTMLElement>('[data-pdp-notify-success]')
  const productTitle = document.querySelector<HTMLElement>('.main-product__title')?.textContent?.trim() ?? ''

  function currentSelection(): string[] {
    return Array.from(options).map((group) => {
      const sel = group.querySelector<HTMLButtonElement>('.is-selected')
      return sel?.dataset.value ?? ''
    })
  }

  function syncVariantFromMediaId(mediaId: number): void {
    if (!variantData) return
    const match = variantData.find((v) => v.featured_media_id === mediaId)
    if (!match) return
    // Mark each option group's swatch matching this variant's option values
    options.forEach((group, i) => {
      const value = match.options[i]
      group.querySelectorAll<HTMLButtonElement>('.main-product__swatch').forEach((sw) => {
        sw.classList.toggle('is-selected', sw.dataset.value === value)
      })
    })
    updateVariant()
  }

  function updateVariant(): void {
    if (!variantData || !variantInput) return
    const selection = currentSelection()
    const match = variantData.find((v) => v.options.every((o, i) => o === selection[i]))
    if (!match) {
      addBtn?.setAttribute('disabled', 'true')
      if (addBtn) {
        addBtn.hidden = false
        addBtn.textContent = 'Unavailable'
      }
      if (notifyForm) notifyForm.hidden = true
      return
    }
    variantInput.value = String(match.id)
    if (priceEl) priceEl.textContent = formatMoney(match.price)
    if (subtitleEl) subtitleEl.textContent = `In ${match.title}`
    activateMediaById(match.featured_media_id)

    if (match.available) {
      addBtn?.removeAttribute('disabled')
      if (addBtn) addBtn.hidden = false
      if (notifyForm) notifyForm.hidden = true
    } else {
      if (addBtn) addBtn.hidden = true
      if (notifyForm) {
        notifyForm.hidden = false
        if (notifyVariant) notifyVariant.value = match.title
        if (notifyVariantId) notifyVariantId.value = String(match.id)
        if (notifyBody) notifyBody.value = `Back-in-stock request — ${productTitle} (${match.title}, ID ${match.id})`
        if (notifySuccess) notifySuccess.hidden = true
      }
    }

    // Update URL so refresh keeps the variant
    const url = new URL(window.location.href)
    url.searchParams.set('variant', String(match.id))
    window.history.replaceState({}, '', url.toString())
  }

  options.forEach((group) => {
    group.addEventListener('click', (e) => {
      const t = e.target as HTMLElement
      const btn = t.closest<HTMLButtonElement>('.main-product__swatch')
      if (!btn) return
      group.querySelectorAll('.main-product__swatch').forEach((x) => x.classList.remove('is-selected'))
      btn.classList.add('is-selected')
      updateVariant()
    })
  })

  notifyForm?.addEventListener('submit', async (e) => {
    e.preventDefault()
    const submitBtn = notifyForm.querySelector<HTMLButtonElement>('button[type="submit"]')
    submitBtn?.setAttribute('disabled', 'true')
    try {
      await fetch('/contact#pdp-notify-form', { method: 'POST', body: new FormData(notifyForm) })
      if (notifySuccess) notifySuccess.hidden = false
      const emailInput = notifyForm.querySelector<HTMLInputElement>('input[type="email"]')
      if (emailInput) emailInput.value = ''
    } catch (err) {
      console.error(err)
    } finally {
      submitBtn?.removeAttribute('disabled')
    }
  })

  const form = pdp.querySelector<HTMLFormElement>('form#pdp-form')
  form?.addEventListener('submit', async (e) => {
    e.preventDefault()
    if (addBtn?.hasAttribute('disabled')) return
    const formData = new FormData(form)
    addBtn?.setAttribute('disabled', 'true')
    try {
      const res = await fetch('/cart/add.js', { method: 'POST', body: formData, headers: { Accept: 'application/json' } })
      if (!res.ok) throw new Error('Add to cart failed')
      window.dispatchEvent(new CustomEvent('meera:cart:open'))
      window.dispatchEvent(new CustomEvent('meera:cart:refresh'))
    } catch (err) {
      console.error(err)
    } finally {
      addBtn?.removeAttribute('disabled')
    }
  })
}

function formatMoney(cents: number): string {
  return '$' + (cents / 100).toFixed(2)
}
